<script setup lang="ts">
import {computed, ref} from 'vue'
import type {DashboardMilestone, DashboardPoint} from '@shared/types'

const props = defineProps<{
  past: DashboardPoint[]
  future: DashboardPoint[]
  optimistic: DashboardPoint[]
  pessimistic: DashboardPoint[]
  total: number
  milestones: DashboardMilestone[]
}>()

const WIDTH = 900
const HEIGHT = 300
const PAD = {top: 16, right: 16, bottom: 26, left: 40}

function toMs(date: string) {
  return new Date(`${date}T00:00:00`).getTime()
}

const domain = computed(() => {
  const all = [...props.past, ...props.future]
  if (!all.length) return null
  return {min: toMs(all[0].date), max: toMs(all[all.length - 1].date)}
})

const xScale = computed(() => {
  if (!domain.value) return (ms: number) => 0
  const min = domain.value.min
  const span = Math.max(1, domain.value.max - domain.value.min)
  const w = WIDTH - PAD.left - PAD.right
  const today = props.past.length ? toMs(props.past[props.past.length - 1].date) : min
  const pastSpan = Math.max(1, today - min)
  const futureSpan = Math.max(1, domain.value.max - today)
  return (ms: number) => {
    if (ms <= today) return PAD.left + ((ms - min) / pastSpan) * w * 0.72
    return PAD.left + w * 0.72 + ((ms - today) / futureSpan) * w * 0.28
  }
})

const chartMax = computed(() => {
  const current = props.past.at(-1)?.value ?? 0
  return Math.max(current > 0 ? current * 5 : props.total * 0.05, 1)
})

const yScalePast = computed(() => {
  const h = HEIGHT - PAD.top - PAD.bottom
  const max = chartMax.value
  return (v: number) => PAD.top + h - (Math.min(v, max) / max) * h
})

const currentValue = computed(() => props.past.at(-1)?.value ?? 0)

const yScaleFuture = (v: number) => {
  const h = HEIGHT - PAD.top - PAD.bottom
  const current = currentValue.value
  if (v <= current) return yScalePast.value(v)
  const remaining = Math.max(props.total - current, 1)
  const progress = Math.min(Math.max(v - current, 0), remaining) / remaining
  return yScalePast.value(current) - progress * (yScalePast.value(current) - PAD.top)
}

function linePath(points: DashboardPoint[], scale: (value: number) => number) {
  if (!points.length) return ''
  const xs = xScale.value
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xs(toMs(p.date)).toFixed(1)},${scale(p.value).toFixed(1)}`)
    .join(' ')
}

function areaPath(points: DashboardPoint[], scale: (value: number) => number) {
  if (!points.length) return ''
  const base = scale(0)
  const first = `${xScale.value(toMs(points[0].date)).toFixed(1)},${base.toFixed(1)}`
  const line = linePath(points, scale).replace(/^M/, 'L')
  return `M${first} ${line} L${xScale.value(toMs(points[points.length - 1].date)).toFixed(1)},${base.toFixed(1)} Z`
}

const pastLine = computed(() => linePath(props.past, yScalePast.value))
const pastArea = computed(() => areaPath(props.past, yScalePast.value))
const futureLine = computed(() => {
  const bridge =
    props.past.length && props.future.length ? [props.past[props.past.length - 1], ...props.future] : props.future
  return linePath(bridge, yScaleFuture)
})
const futureArea = computed(() =>
  areaPath([...(props.past.length ? [props.past[props.past.length - 1]] : []), ...props.future], yScaleFuture)
)
const optimisticLine = computed(() =>
  linePath([...(props.past.length ? [props.past[props.past.length - 1]] : []), ...props.optimistic], yScaleFuture)
)
const pessimisticLine = computed(() =>
  linePath([...(props.past.length ? [props.past[props.past.length - 1]] : []), ...props.pessimistic], yScaleFuture)
)

const todayX = computed(() => (props.past.length ? xScale.value(toMs(props.past[props.past.length - 1].date)) : null))

const yTicks = computed(() =>
  [0, 0.25, 0.5, 0.75, 1].map((f) => ({
    value: Math.round(f * chartMax.value),
    y: yScalePast.value(f * chartMax.value),
    label: `${Math.round(((f * chartMax.value) / Math.max(props.total, 1)) * 100)}%`,
  }))
)

const xLabels = computed(() => {
  const all = [...props.past, ...props.future]
  if (all.length < 2) return []
  const count = 6
  const labels: Array<{x: number; label: string}> = []
  for (let i = 0; i < count; i++) {
    const idx = Math.round((i / (count - 1)) * (all.length - 1))
    const p = all[idx]
    labels.push({x: xScale.value(toMs(p.date)), label: formatAxisDate(p.date)})
  }
  return labels
})

const visibleMilestones = computed(() => {
  if (!domain.value || !props.future.length) return []
  const currentPercent = (currentValue.value / Math.max(props.total, 1)) * 100
  const nextPercent = Math.min(100, Math.ceil((currentPercent + 0.001) / 20) * 20)
  const result: Array<{percent: number; x: number; y: number; date: string; achieved: boolean}> = []
  for (const m of props.milestones) {
    if (m.percent !== nextPercent && m.percent !== 100) continue
    if (!m.date) continue
    const ms = toMs(m.date)
    if (ms > domain.value.max || ms < domain.value.min) continue
    const value = (m.percent / 100) * props.total
    result.push({
      percent: m.percent,
      x: xScale.value(ms),
      y: yScaleFuture(value),
      date: m.date,
      achieved: m.etaDays === 0,
    })
  }
  return result
})

const hover = ref<{x: number; px: number; point: DashboardPoint; isFuture: boolean} | null>(null)

function onMove(event: MouseEvent) {
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  const ratio = (event.clientX - rect.left) / rect.width
  const svgX = ratio * WIDTH
  const xs = xScale.value
  const all = [
    ...props.past.map((p) => ({...p, isFuture: false})),
    ...props.future.map((p) => ({...p, isFuture: true})),
  ]
  if (!all.length) return
  let nearest = all[0]
  let bestDist = Infinity
  for (const p of all) {
    const dist = Math.abs(xs(toMs(p.date)) - svgX)
    if (dist < bestDist) {
      bestDist = dist
      nearest = p
    }
  }
  hover.value = {
    x: xs(toMs(nearest.date)),
    px: ratio * rect.width,
    point: nearest,
    isFuture: nearest.isFuture,
  }
}

const tooltipStyle = computed(() => {
  if (!hover.value) return {}
  const flip = hover.value.px > 150
  return {
    left: `${hover.value.px}px`,
    transform: flip ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
  }
})

const futureTicks = [0, 25, 50, 75, 100].map((percent) => ({
  percent,
  y: yScaleFuture((percent / 100) * props.total),
}))

const dateFormatter = new Intl.DateTimeFormat(undefined, {month: 'long', day: 'numeric', year: 'numeric'})

function formatAxisDate(value: string): string {
  const date = new Date(`${value}T00:00:00`)
  const now = new Date()
  if (date.getFullYear() !== now.getFullYear()) {
    if (date.getTime() - now.getTime() > 2 * 365 * 24 * 60 * 60 * 1000) {
      return new Intl.DateTimeFormat(undefined, {year: 'numeric'}).format(date)
    }
    return new Intl.DateTimeFormat(undefined, {month: 'short', year: 'numeric'}).format(date)
  }
  return new Intl.DateTimeFormat(undefined, {month: 'short', day: 'numeric'}).format(date)
}
</script>

<template>
  <div class="relative w-full select-none" @mousemove="onMove" @mouseleave="hover = null">
    <svg :viewBox="`0 0 ${WIDTH} ${HEIGHT}`" class="block w-full" role="img">
      <defs>
        <linearGradient id="dash-past-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="var(--color-chart-2)" stop-opacity="0.35" />
          <stop offset="100%" stop-color="var(--color-chart-2)" stop-opacity="0.02" />
        </linearGradient>
        <linearGradient id="dash-future-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="var(--color-chart-1)" stop-opacity="0.18" />
          <stop offset="100%" stop-color="var(--color-chart-1)" stop-opacity="0.01" />
        </linearGradient>
      </defs>

      <g v-for="tick in yTicks" :key="tick.label">
        <line
          :x1="PAD.left"
          :x2="todayX ?? WIDTH - PAD.right"
          :y1="tick.y"
          :y2="tick.y"
          class="stroke-border"
          stroke-dasharray="3 5" />
        <text :y="tick.y - 4" :x="PAD.left - 8" text-anchor="end" class="fill-muted-foreground text-[11px]">
          {{ tick.label }}
        </text>
      </g>
      <g v-for="tick in futureTicks" :key="`future-${tick.percent}`">
        <line
          v-if="todayX !== null"
          :x1="todayX"
          :x2="WIDTH - PAD.right"
          :y1="tick.y"
          :y2="tick.y"
          class="stroke-border/60"
          stroke-dasharray="3 5" />
        <text
          v-if="todayX !== null"
          :x="WIDTH - 4"
          :y="tick.y + 4"
          text-anchor="end"
          class="fill-muted-foreground text-[11px]">
          {{ tick.percent }}%
        </text>
      </g>

      <path :d="futureArea" fill="url(#dash-future-fill)" />
      <path :d="pastArea" fill="url(#dash-past-fill)" />
      <path
        :d="pessimisticLine"
        fill="none"
        stroke="var(--color-chart-4)"
        stroke-width="1.5"
        stroke-dasharray="4 5"
        stroke-linecap="round"
        opacity="0.9" />
      <path
        :d="optimisticLine"
        fill="none"
        stroke="var(--color-chart-3)"
        stroke-width="1.5"
        stroke-dasharray="4 5"
        stroke-linecap="round"
        opacity="0.9" />
      <path
        :d="futureLine"
        fill="none"
        stroke="var(--color-chart-1)"
        stroke-width="2"
        stroke-dasharray="6 5"
        stroke-linecap="round" />
      <path :d="pastLine" fill="none" stroke="var(--color-chart-2)" stroke-width="2.5" stroke-linecap="round" />

      <line
        v-if="todayX !== null"
        :x1="todayX"
        :x2="todayX"
        :y1="PAD.top"
        :y2="HEIGHT - PAD.bottom"
        class="stroke-muted-foreground/60"
        stroke-width="1" />
      <text
        v-if="todayX !== null"
        :x="todayX + 4"
        :y="HEIGHT - PAD.bottom - 6"
        class="fill-muted-foreground text-[10px]">
        {{ $t('home.dashboard.today') }}
      </text>
      <text
        v-if="todayX !== null && future.length"
        :x="todayX + 8"
        :y="PAD.top + 12"
        class="fill-muted-foreground text-[10px]">
        {{ $t('home.dashboard.forecast') }} →
      </text>

      <g v-for="m in visibleMilestones" :key="m.percent">
        <circle :cx="m.x" :cy="m.y" r="9" fill="var(--color-card)" stroke="var(--color-chart-1)" stroke-width="1.5" />
        <text :x="m.x" :y="m.y + 3.5" text-anchor="middle" class="fill-foreground text-[9px] font-semibold">
          {{ m.percent }}
        </text>
      </g>

      <template v-for="(label, i) in xLabels" :key="i">
        <text :x="label.x" :y="HEIGHT - 8" text-anchor="middle" class="fill-muted-foreground text-[10px]">
          {{ label.label }}
        </text>
      </template>

      <g v-if="hover">
        <line :x1="hover.x" :x2="hover.x" :y1="PAD.top" :y2="HEIGHT - PAD.bottom" class="stroke-foreground/30" />
        <circle
          :cx="hover.x"
          :cy="hover.isFuture ? yScaleFuture(hover.point.value) : yScalePast(hover.point.value)"
          r="4"
          :fill="hover.isFuture ? 'var(--color-chart-1)' : 'var(--color-chart-2)'"
          class="stroke-background"
          stroke-width="1.5" />
      </g>
    </svg>

    <div
      v-if="hover"
      class="pointer-events-none absolute top-2 z-10 min-w-36 rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md"
      :style="tooltipStyle">
      <div class="font-medium">{{ dateFormatter.format(new Date(`${hover.point.date}T00:00:00`)) }}</div>
      <div class="mt-0.5 text-muted-foreground">
        {{ hover.point.value }} / {{ total }}
        <span v-if="hover.isFuture"> · {{ $t('home.dashboard.forecast') }}</span>
      </div>
    </div>
  </div>
</template>
